import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { UserPermissionsService } from '../services/user-permissions.service';
import { InvoiceService } from '../services/invoice.service';
import { environment } from '../../environments/environment';

/**
 * Guard que verifica se o usuário possui a permissão necessária para acessar uma rota
 * @param permissionKey A chave da permissão necessária (ex: 'dashboards.view')
 * @returns true se o usuário possui a permissão, false caso contrário (redireciona para no-permission)
 */
export const permissionGuard = (permissionKey: string): CanActivateFn => {
  return (route, state) => {
    const permissionsService = inject(UserPermissionsService);
    const router = inject(Router);
    const invoiceService = inject(InvoiceService);

    // Verificar se há token antes de verificar permissões
    if (typeof window === 'undefined' || !window.localStorage) {
      return router.createUrlTree(['/login']);
    }

    const token = localStorage.getItem('jwt') || localStorage.getItem('jwt-password');
    if (!token) {
      return router.createUrlTree(['/login']);
    }

    // Carregar permissões de forma assíncrona e verificar
    return new Promise<boolean | import('@angular/router').UrlTree>((resolve) => {
      const checkPermission = () => {
        if (permissionsService.hasPermission(permissionKey)) {
          resolve(true);
          return;
        }
        if (permissionKey === 'invoices.view') {
          invoiceService.getOverdueInvoices().subscribe({
            next: (response) => {
              if (response.blockAccess === true) {
                resolve(true);
                return;
              }
              resolveToNoPermissionOrRedirect();
            },
            error: () => resolveToNoPermissionOrRedirect()
          });
          return;
        }
        resolveToNoPermissionOrRedirect();
      };

      const resolveToNoPermissionOrRedirect = () => {
        if (state.url === '/no-permission') {
          resolve(true);
        } else {
          const accessibleRoute = permissionsService.getFirstAccessibleRoute();
          if (accessibleRoute) {
            if (!environment.production) {
              console.debug(`Acesso negado para '${permissionKey}', redirecionando para primeira rota acessível: ${accessibleRoute}`);
            }
            resolve(router.createUrlTree([accessibleRoute]));
          } else {
            if (!environment.production) {
              console.debug(`Acesso negado: usuário não possui permissão '${permissionKey}' e não tem acesso a nenhuma rota`);
            }
            resolve(router.createUrlTree(['/no-permission']));
          }
        }
      };

      permissionsService.loadUserPermissions()
        .then(() => checkPermission())
        .catch((error) => {
          if (!environment.production) {
            console.error('Erro ao carregar permissões no guard:', error);
          }
          resolve(router.createUrlTree(['/login']));
        });
    });
  };
};
